<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import type { Subject } from '@/types/Subject'
  import Byline from '@/components/common/Byline.vue'
  import Typography from '@/components/common/Typography.vue'
  import ImageWithCaption from '@/components/common/ImageWithCaption.vue'
  import { useImage } from '@/composables/useImage'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import { useAuthorsQuery } from '@/composables/useAuthorsQuery'

  const props = defineProps<{subject?: Subject }>()
  const authors = useAuthorsQuery(props.subject?.authorIds)
  const { src, altText, caption, copyright } = useImage({ imageId: props.subject?.image?.destinationContentId ?? '' })

  const { t } = useI18n()
</script>

<template>
  <div :class="['subject-hero-wrapper', subject?.colorPair]">
    <div class="subject-hero">
      <div class="image-container">
        <div class="image-content">
          <ImageWithCaption
            v-if="src"
            :src="src"
            :alt-text="altText || ''"
            :caption="caption"
            :copyright="copyright"
            :object-fit="'cover'"
            aspect-ratio="3/2"
            image-variation="banner_large"
            class="image"
          />
        </div>
      </div>
      <div class="text-container">
        <div class="text-content">
          <div class="content">
            <Typography
              v-if="subject?.label"
              variant="label"
            >
              {{ subject?.label }}
            </Typography>
            <Typography variant="h1">
              {{ subject?.title }}
            </Typography>
            <Typography v-if="subject?.subtitle" variant="preamble">
              {{ subject?.subtitle }}
            </Typography>
            <RichTextRenderer
              v-if="subject?.description"
              :rich-text="subject?.description.xhtml5output"
            />
            <slot name="buttons" />
          </div>
          <Byline
            v-if="authors?.data?.value?.length"
            :authors="authors.data.value"
            :prefix="t('authors')"
            variant="text-only"
            no-lines
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @include color-pairs;

  .subject-hero-wrapper {
    background: linear-gradient(to left, var(--color-pair-background-color) 50%, var(--color-pair-color) 50%);

    .subject-hero {
      display: flex;
      min-height: 600px;

      @include vgs-media-mobile {
        flex-direction: column-reverse;
        min-height: unset;
      }
    }

    .image-container,
    .text-container {
      flex: 1;
    }

    .image-container {
      .image-content {
        @include vgs-padding-image-element;

        display: flex;
        justify-content: center;
        align-items: center;
        background: var(--color-pair-color);
        height: 100%;
      }
    }

    .text-container {
      .text-content {
        @include vgs-padding-text-element;

        height: 100%;
        display: flex;
        flex-direction: column;
        gap: 16px;
        color: var(--color-pair-color);
        background: var(--color-pair-background-color);
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 16px;
      justify-content: center;
      max-width: 75ch;

      * {
        margin: 0;
      }
    }
  }

  .image {
    height: 100%;
  }
</style>
